import axios from "axios";
import React, { useContext, useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import { FiEye } from "react-icons/fi";
import UserContext from "../../contexts/UserContext";
import { useNavigate } from "react-router-dom";
import authenticate from "../../utils/Authenticate";
import displayError from "../../utils/ErrorHandler";

const CreateMenu = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ name: "" });
  const [dishes, setDishes] = useState([{ id: Date.now(), dishId: "", quantity: "", ingredients: [], showIngredients: false }]);
  const [allDishes, setAllDishes] = useState([]);
  const [dishOptions, setDishOptions] = useState([]);
  const [notification, setNotification] = useState({ message: "", type: "" });

  useEffect(() => {
    async function fetchDishes() {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URI}/api/menu/getAllDishes`, { withCredentials: true });
        setAllDishes(response.data.data);
        setDishOptions(
          response.data.data.map(dish => ({ value: dish._id, label: dish.name, raw: dish }))
        );
      } catch (error) {
        console.error("Error fetching dishes:", error);
      }
    }
    fetchDishes();
  }, []);

  useEffect(() => {
    async function startup() {
      if (!user) await authenticate(setUser, navigate);
    }
    startup();
  }, [user, navigate, setUser]);

  const fetchIngredients = async (dishId, index) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URI}/api/dish/viewdishWithIngridents?id=${dishId}`, { withCredentials: true });
      const updatedDishes = [...dishes];
      updatedDishes[index].ingredients = res.data.data.ingredients;
      setDishes(updatedDishes);
    } catch (err) {
      console.error("Failed to fetch ingredients:", err);
    }
  };

  const handleDishChange = async (index, field, value) => {
    const updatedDishes = [...dishes];
    updatedDishes[index][field] = value;
    updatedDishes[index].ingredients = [];
    updatedDishes[index].showIngredients = false;
    setDishes(updatedDishes);
    if (field === "dishId" && value) {
      await fetchIngredients(value, index);
    }
  };

  const addDishField = () => {
    setDishes(prev => [...prev, { id: Date.now(), dishId: "", quantity: "", ingredients: [], showIngredients: false }]);
  };

  const removeDishField = (index) => {
    setDishes(prev => prev.filter((_, i) => i !== index));
  };

  const toggleIngredients = async (index) => {
    const updatedDishes = [...dishes];
    updatedDishes[index].showIngredients = !updatedDishes[index].showIngredients;
    setDishes(updatedDishes);
    if (updatedDishes[index].dishId && updatedDishes[index].showIngredients && updatedDishes[index].ingredients.length === 0) {
      await fetchIngredients(updatedDishes[index].dishId, index);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasDuplicate = new Set(dishes.map(d => d.dishId)).size !== dishes.length;
    if (hasDuplicate) {
      setNotification({ message: "Duplicate dishes not allowed.", type: "error" });
      return;
    }

    const menuData = {
      name: formData.name,
      dishes: dishes.map(({ dishId, quantity }) => ({ dishId, quantity }))
    };

    try {
      const res = await axios.post("/api/menu/add", menuData, { withCredentials: true });
      setNotification({ message: "Menu created successfully!", type: "success" });
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      setNotification({ message: "Failed to create menu.", type: "error" });
    }
  };

  return (
    <div className="wrapper">
      <div className="w-full">
        <h2 className="text-base font-bold bg-lime-800 text-white p-3 py-4 text-center">Create Menu</h2>
        {notification.message && (
          <div className={`p-3 mb-4 text-center ${notification.type === "error" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>{notification.message}</div>
        )}
      </div>

      <div className="p-4 max-w-2xl mx-auto">
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter Menu name"
            className="mb-4 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md"
            required
          />

          <h3 className="font-semibold text-gray-800 mb-2">Dishes</h3>
          {dishes.map((dish, index) => (
            <div key={dish.id} className="mb-4">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <AsyncSelect
                    loadOptions={(inputValue) => {
                      const selectedIds = dishes.map((d, i) => i !== index && d.dishId).filter(Boolean);
                      const filtered = allDishes.filter(d => !selectedIds.includes(d._id));
                      return Promise.resolve(
                        filtered
                          .filter(d => d.name.toLowerCase().includes((inputValue || "").toLowerCase()))
                          .map(d => ({ value: d._id, label: d.name }))
                      );
                    }}
                    defaultOptions={allDishes
                      .filter(d => !dishes.some(ds => ds.dishId === d._id))
                      .map(d => ({ value: d._id, label: d.name }))}
                    onChange={(selected) => handleDishChange(index, "dishId", selected.value)}
                    value={dish.dishId ? { value: dish.dishId, label: allDishes.find(d => d._id === dish.dishId)?.name || "" } : null}
                    placeholder="Search and select a dish..."
                  />
                </div>
                {dish.dishId && (
                  <button type="button" onClick={() => toggleIngredients(index)} className="text-blue-600">
                    <FiEye className="w-5 h-5" />
                  </button>
                )}
              </div>

              {dish.showIngredients && dish.ingredients?.length > 0 && (
                <div className="mt-2 bg-gray-50 p-2 rounded border text-sm text-gray-700">
                  <p className="font-semibold mb-1">Ingredients:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    {dish.ingredients.map((ing, i) => (
                      <li key={i}>{ing.name?.name || "Unknown"}</li>
                    ))}
                  </ul>
                </div>
              )}

              {dishes.length > 1 && (
                <button type="button" onClick={() => removeDishField(index)} className="text-red-600 text-sm mt-1">Remove</button>
              )}
            </div>
          ))}

          <button type="button" onClick={addDishField} className="mb-4 text-blue-600 text-sm">+ Add Another Dish</button>

          <button type="submit" className="w-full py-3 px-4 rounded-md text-white bg-lime-800 hover:bg-lime-700">Submit Menu</button>
        </form>
      </div>
    </div>
  );
};

export default CreateMenu;
